import React from 'react';
import {Link} from 'react-router-dom';
import styled from 'styled-components';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {ManuscriptDisclosure} from '../../components/ai/DisclosureControls';
import BookFormatBadge from '../../components/book/bookFormatBadge';
import {imageUtil, uxAnalyticsUtil} from '../../utils';
import {useDiscoveryExperimentTracking} from './experimentClient';

const Row = styled.article`
  display: grid;
  grid-template-columns: 66px minmax(0, 1fr);
  gap: 12px;
  min-width: 0;
  padding: 11px;
  border: 1px solid ${({theme}) => theme.navigation.border};
  border-radius: 12px;
  background: ${({theme}) => theme.navigation.surface};
  color: ${({theme}) => theme.navigation.heading};
  @media (max-width: 767px) {
    grid-template-columns: 58px minmax(0, 1fr);
    gap: 10px;
    padding: 9px;
  }
`;
const Cover = styled.img`
  display: block;
  width: 66px;
  height: 92px;
  object-fit: cover;
  border-radius: 6px;
  background: ${({theme}) => theme.navigation.raisedSurface};
  @media (max-width: 767px) { width: 58px; height: 82px; }
`;
const Title = styled(Link)`
  color: ${({theme}) => theme.navigation.heading};
  font-weight: 800;
  font-size: 0.98rem;
  line-height: 1.2;
  text-decoration: none;
  &:hover { color: ${({theme}) => theme.navigation.accent}; text-decoration: underline; }
  &:focus-visible { outline: 3px solid ${({theme}) => theme.navigation.focus}; outline-offset: 3px; }
`;
const Byline = styled.div`
  margin-top: 3px;
  color: ${({theme}) => theme.navigation.text};
  font-size: 0.79rem;
`;
const Meta = styled.div`
  margin-top: 5px;
  color: ${({theme}) => theme.navigation.mutedText};
  font-size: 0.74rem;
`;
const Description = styled.p`
  margin: 6px 0 0;
  color: ${({theme}) => theme.navigation.text};
  font-size: 0.79rem;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
const Badges = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 6px;
`;
const GenreButton = styled.button`
  border: 0;
  background: none;
  color: ${({theme}) => theme.navigation.accent};
  padding: 6px 6px 6px 0;
  font: inherit;
  font-size: 0.73rem;
  font-weight: 700;
  cursor: pointer;
  &:hover { text-decoration: underline; }
  &:focus-visible { outline: 3px solid ${({theme}) => theme.navigation.focus}; }
`;

const DEFAULT_COVER = 'https://storage.googleapis.com/beta-reader-prod.appspot.com/defaults/DEFAULT_COVER_BACKGROUND.png';

const DiscoveryCard = ({book, compact = false, onGenre, source}) => {
  const trackExperiment = useDiscoveryExperimentTracking();
  const {t, i18n} = useTranslation();
  const path = `/books/${book._id}`;
  const coverUrl = book.cover?.highRes?.url || DEFAULT_COVER;
  let language = book.language || '';
  try {
    language = new Intl.DisplayNames([i18n.language], {type: 'language'}).of(language) || language;
  } catch { language = book.language || ''; }
  const trackOpen = () => {
    try {
      sessionStorage.setItem('br-discovery-return', JSON.stringify({
        bookId: String(book._id),
        path: window.location.pathname + window.location.search,
        savedAt: Date.now()
      }));
    } catch { /* Storage can be disabled. */ }
    uxAnalyticsUtil.trackEvent({category: 'Discover', action: 'opened-book', label: source || 'results'});
    trackExperiment('book_open');
  };
  return <Row>
    <Link to={path} onClick={trackOpen} aria-label={`${t('DiscoveryOpenBook')}: ${book.title}`}>
      <Cover
        src={imageUtil.getImageUrl({operation: 'width=132', originalImageUrl: coverUrl})}
        alt=""
        loading={compact ? 'lazy' : 'eager'}
      />
    </Link>
    <div>
      <Title to={path} onClick={trackOpen}>{book.title}</Title>
      <Byline>{t('by')} {book.authorName}</Byline>
      <Meta>{language}{book.wordCount !== undefined ? ` · ${new Intl.NumberFormat(i18n.language).format(book.wordCount)} ${t('DiscoveryWords')}` : ''}</Meta>
      {!compact && <>
        <Description>{book.description}</Description>
        <Badges>
          <ManuscriptDisclosure book={book} />
          <BookFormatBadge audioAvailable={Boolean(book.audioAvailable)} />
        </Badges>
        <div>{(book.genres || []).slice(0, 2).map(genre =>
          <GenreButton key={genre} type="button" onClick={() => onGenre(genre)}>{genre}</GenreButton>
        )}</div>
      </>}
    </div>
  </Row>;
};

DiscoveryCard.propTypes = {
  book: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.string,
    authorName: PropTypes.string,
    language: PropTypes.string,
    wordCount: PropTypes.number,
    description: PropTypes.string,
    audioAvailable: PropTypes.bool,
    genres: PropTypes.arrayOf(PropTypes.string),
    cover: PropTypes.shape({highRes: PropTypes.shape({url: PropTypes.string})})
  }).isRequired,
  compact: PropTypes.bool,
  onGenre: PropTypes.func,
  source: PropTypes.string
};

export default DiscoveryCard;
